import type { IdentityEndpointDefinition, IdentityPolicyApi } from './contract';

/**
 * The public api members this module calls. Every one is public RTKQ API — `internalActions` (notably
 * `removeQueryResult`) is deliberately NOT used, so an automated dependency bump cannot break the purge on
 * an unstable internal contract.
 */
interface IdentityApiAccess {
    enhanceEndpoints(enhancement: { endpoints: Record<string, (definition: IdentityEndpointDefinition | undefined) => void> }): unknown;
    /**
     * The generated per-endpoint bridge — `initiate` is how the reconcile forces every repair (see
     * `repairEndpoint`), `select` is how the repair listener reads back the entry a rejection left behind, and
     * `matchRejected` is how it attributes a rejected action to exactly this api + endpoint.
     */
    endpoints: Record<
        string,
        {
            initiate(arg: unknown, options?: { subscribe?: boolean; forceRefetch?: boolean }): unknown;
            select(arg: unknown): (state: unknown) => { isError?: unknown; error?: unknown };
            /** RTKQ's generated matcher for this endpoint's rejected query thunk — same family as `matchFulfilled`. */
            matchRejected(action: unknown): boolean;
        }
    >;
    util: {
        selectCachedArgsForQuery(state: unknown, endpointName: string): unknown[];
        updateQueryData(endpointName: string, arg: unknown, recipe: (draft: unknown) => unknown): unknown;
        /**
         * A THUNK: dispatching it yields the request currently in flight for exactly this endpoint + arg, or
         * `undefined` when none is. How a repair finds the request it has to displace — see `repairEndpoint`.
         */
        getRunningQueryThunk(endpointName: string, arg: unknown): unknown;
    };
}

/**
 * What dispatching `getRunningQueryThunk` yields: the in-flight request's `initiate` promise.
 *
 * It RESOLVES (with the resulting cache entry) and never rejects — including when aborted — so awaiting it needs
 * no catch. Narrowed to the two members the purge uses.
 */
export interface RunningQuery extends PromiseLike<unknown> {
    /** The reason becomes the rejection's `message`, which is how the abort stays classifiable — see `refetchAfterAbort`. */
    abort(reason?: string): void;
}

/**
 * RTK types `enhanceEndpoints` and every `api.util` helper against its own `Definitions`/`RootState`
 * generics — a per-endpoint string-literal keyspace and a per-api root state that no structural declaration
 * can stand in for (and that this module deliberately does not import RTK to obtain). One cast here, at the
 * single boundary where those helpers are reached, keeps every call site in the engine modules plainly typed.
 */
export function apiAccess(api: IdentityPolicyApi): IdentityApiAccess {
    return api as unknown as IdentityApiAccess;
}

/**
 * Yield until every consumer that JOINED the displaced request has resolved off the cache entry, before the
 * repair flips that entry back to `pending`.
 *
 * TWO callers, for the same reason from opposite directions: `refetchAfterAbort` (the abort path, where the
 * displaced request is one the reconcile killed) and `letAwaitingConsumersSettle` (the rejection-repair path,
 * where it is one the fence rejected). Both need the same bound, so both spend the same hops here.
 *
 * Why a consumer can be attached at all: a plain `initiate()` dispatched while the displaced request is still
 * pending is bailed out by RTKQ's query condition, and RTKQ resolves it with
 * `Promise.all([runningQuery, thunkResult]).then(selectFromState)` — it re-READS the entry a couple of
 * microtasks AFTER the displaced promise settles and reports whatever it finds there. Refetching in the first
 * of those microtasks (what awaiting the displaced promise alone gets you) means that read lands on
 * `pending` + the empty patch — and `unwrap()` does not treat `pending` as a failure, so it hands that consumer
 * the empty patch as a SUCCESSFUL result. For a one-shot accessor an empty list is a real answer ("no rows"),
 * which is the false success this whole path exists to prevent. Yielding past the read instead leaves it on the
 * entry's REJECTED state, so `unwrap()` throws and the consumer follows the documented contract (treat as
 * loading, retry after the commit signal) rather than acting on data that was never fetched.
 *
 * The cost is that the entry rests at rejected-and-empty for a few extra microtasks before the repair's
 * `pending` lands — the same state it already passed through, just wider. Microtasks, not network time, and the
 * data is already emptied, so nothing of the previous identity is exposed by the delay.
 *
 * COUPLED TO RTKQ'S JOIN SHAPE by hop count, deliberately: nothing public reports "every promise attached to
 * that request has settled". The guard against a dependency bump changing the shape is the spec that dispatches
 * a plain `initiate()` into this window and asserts a rejection — it fails loudly instead of the race quietly
 * reopening.
 */
export async function letJoinedConsumersSettle(): Promise<void> {
    await Promise.resolve();
    await Promise.resolve();
}
